import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Button } from '@zillow/constellation';

// context
import { RMWContext } from 'app/shared/providers/RMWContext';

// constants
import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';

import {
    GA_EVENTS,
    GA_PREFIX,
} from 'app/shared/constants/AnalyticsListingModuleConstants';

import { LISTING_SUMMARY_DISPLAY_STATUS } from 'app/shared/constants/ListingSummaryConstants';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import stringHelper from 'app/shared/utils/stringHelper';

// style
import {
    StyledButton,
    StyledButtonGroup,
    StyledCTAComponent,
    StyledVONContainer,
} from './StyledComponents';

const propTypes = {
    listingAlias: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    vonComponent: PropTypes.node,
};

const defaultProps = {
    vonComponent: null,
};

const getCTAPath = (listingAlias, paths) => {
    const { propertiesPage } = paths;

    return `${propertiesPage}/${listingAlias}/listing`;
};

const CTA = ({
    canDeactivate,
    listingAlias,
    status,
    vonComponent,
}) => {
    const { paths } = React.useContext(RMWContext);
    const dispatch = useDispatch();

    const { DEACTIVATE_LISTING_CLICK, EDIT_LISTING_CLICK, LIST_MY_PROPERTY_CLICK } = GA_EVENTS;
    const { DEACTIVATE_LISTING, EDIT_LISTING, LIST_MY_PROPERTY } = GA_PREFIX;

    const gaFormattedStatus = stringHelper.startCaseRemoveSpace(status);

    const ctaPath = getCTAPath(listingAlias, paths);

    const gotoEditListingPage = (queryParam = '') => {
        window.router.transitionTo(`${ctaPath}${queryParam}`);
    };

    const handleEditClick = () => {
        dispatch(analyticsUtils.track.GAEventV2({
            ...EDIT_LISTING_CLICK,
            action: `${EDIT_LISTING}${DELIMITER}${gaFormattedStatus}`,
        }));

        gotoEditListingPage();
    };

    const handleDeactivateClick = () => {
        dispatch(analyticsUtils.track.GAEventV2({
            ...DEACTIVATE_LISTING_CLICK,
            action: `${DEACTIVATE_LISTING}${DELIMITER}${gaFormattedStatus}`,
        }));

        gotoEditListingPage('?action=deactivate');
    };

    const handleListMyPropertyClick = () => {
        dispatch(analyticsUtils.track.GAEventV2({
            ...LIST_MY_PROPERTY_CLICK,
            action: `${LIST_MY_PROPERTY}${DELIMITER}${gaFormattedStatus}`,
        }));

        gotoEditListingPage();
    };

    const renderCTA = () => {
        if (status === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE) {
            return (
                <Button
                    buttonType="primary"
                    fluid={true}
                    onClick={handleListMyPropertyClick}
                >
                    List my property
                </Button>
            );
        }

        return (
            <StyledButtonGroup>
                <StyledButton
                    buttonType="secondary"
                    fluid={true}
                    onClick={handleEditClick}
                >
                    Edit
                </StyledButton>

                <StyledButton
                    buttonType="primary"
                    disabled={!canDeactivate}
                    fluid={true}
                    onClick={handleDeactivateClick}
                >
                    Deactivate
                </StyledButton>
            </StyledButtonGroup>
        );
    };

    return (
        <StyledCTAComponent className="CTA">
            {renderCTA()}

            {vonComponent && (
                <StyledVONContainer>
                    {vonComponent}
                </StyledVONContainer>
            )}
        </StyledCTAComponent>
    );
};

CTA.propTypes = propTypes;
CTA.defaultProps = defaultProps;

export default CTA;
